import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import {
  createAssessmentItem,
  deleteAssessmentItem,
  updateAssessmentItem,
} from '@/data/services/assessmentItemServices';
import { Assessment, AssessmentItem } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import useTemplatePropagation from './useTemplatePropagation';

type UseAssessmentItemsProps = {
  evaluation: Evaluation;
  assessment: Assessment;
  cacheManager: ListCacheManager<Evaluation>;
};

type UpdateMutationArgs = {
  itemId: number;
  data: Partial<AssessmentItem>;
};

const useAssessmentItems = ({
  evaluation,
  assessment,
  cacheManager,
}: UseAssessmentItemsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const items = assessment.items ?? [];

  const { propagate } = useTemplatePropagation({ evaluation });

  const updateAssessmentItems = (nextItems: AssessmentItem[]) => {
    const currentEvaluation = cacheManager.getById(evaluation.id);
    if (!currentEvaluation) return;

    cacheManager.update({
      id: currentEvaluation.id,
      assessments: (currentEvaluation.assessments ?? []).map(a =>
        a.id === assessment.id ? { ...a, items: nextItems } : a,
      ),
    });
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<AssessmentItem>) =>
      createAssessmentItem(evaluation.id, assessment.id, data),
    onSuccess: newItem => {
      updateAssessmentItems([...items, newItem]);
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ itemId, data }: UpdateMutationArgs) =>
      updateAssessmentItem(evaluation.id, assessment.id, itemId, data),
    onSuccess: updated => {
      updateAssessmentItems(
        items.map(item => (item.id === updated.id ? updated : item)),
      );
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (itemId: number) => {
      await deleteAssessmentItem(evaluation.id, assessment.id, itemId);
      await propagate();
    },
    onSuccess: (_, itemId) => {
      updateAssessmentItems(items.filter(item => item.id !== itemId));
      alert.success(t('deleteAssessmentItemSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    items,
    createItem: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateItem: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteItem: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useAssessmentItems;
